import React from 'react';
import { TIMEZONE_MAP, TIMEZONE_NAME_MAP } from './timezone_data';
import { Modal } from 'ui/base/modal';
import { TimezoneStore } from './timezone_store';
import classnames from 'classnames';
import BaseStyles from './timezone_input.module.css';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { TextInput as SearchInput } from 'ui/base/input';
import { ScrollableArea } from 'ui/base/page/scrollable_area';
import { BackButton } from 'ui/base/button/back_button';
import { observer } from 'mobx-react';
import { ButtonClickEvent } from 'ui/base/types';

type TimezoneInputStyles = {
  timezoneButton: string;
  placeholder: string;
  textInput: string;
  modalContainer: string;
  column: string;
  headerContainer: string;
  listContainer: string;
  listItem: string;
};

const themedStyles: ThemedStyles<TimezoneInputStyles> = {
  [Theme.DAYLIGHT]: {
    timezoneButton: BaseStyles.daylightTimezoneButton,
    placeholder: BaseStyles.daylightPlaceholder,
    textInput: BaseStyles.daylightTextInput,
    modalContainer: BaseStyles.modalContainer,
    column: BaseStyles.column,
    headerContainer: BaseStyles.headerContainer,
    listContainer: BaseStyles.listContainer,
    listItem: BaseStyles.listItemDaylight,
  },
  [Theme.MIDNIGHT]: {
    timezoneButton: BaseStyles.midnightTimezoneButton,
    placeholder: BaseStyles.midnightPlaceholder,
    textInput: BaseStyles.midnightTextInput,
    modalContainer: BaseStyles.modalContainer,
    column: BaseStyles.column,
    headerContainer: BaseStyles.headerContainer,
    listContainer: BaseStyles.listContainer,
    listItem: BaseStyles.listItemMidnight,
  },
};

type TimezoneOptionProps = {
  store: TimezoneStore;
  name: string;
  label: string;
  offsetString: string;
};

const TimezoneOption = observer(({
  store,
  name,
  label,
  offsetString,
}: TimezoneOptionProps) => {
  const styles = useStyles(themedStyles);
  const setValue = React.useCallback((event: ButtonClickEvent) => {
    event.stopPropagation();
    store.name.setValue(name);
    store.label.setValue(label);
    store.offset.setValue(offsetString);
    store.modal.hideModal();
  }, [store, label, name, offsetString]);
  return (
    <button type='button' className={styles.listItem} onClick={setValue}>
      <div>{label}</div>
      <div>{offsetString}</div>
    </button>
  );
});

type TimezoneListProps = {
  store: TimezoneStore;
};

const TimezoneList = observer(({ store }: TimezoneListProps) => {
  const styles = useStyles(themedStyles);
  return (
    <div className={styles.listContainer}>
      {
        TIMEZONE_MAP.filter(item => (
          item.lower.indexOf(store.search.value.trim().toLowerCase()) >= 0
        )).map((timezone, index) => (
          <TimezoneOption
            key={index}
            store={store}
            name={timezone.name}
            label={timezone.label}
            offsetString={timezone.offsetString}
          />
        ))
      }
    </div>
  );
});

type TimezoneModalContentProps = {
  placeholder: string;
  store: TimezoneStore;
};

const TimezoneModalContent = ({ store, placeholder }: TimezoneModalContentProps) => {
  const styles = useStyles(themedStyles);
  return (
    <div className={styles.modalContainer}>
      <ScrollableArea>
        <div className={styles.column}>
          <div className={styles.headerContainer}>
            <BackButton onClick={store.modal.hideModal}/>
            <SearchInput
              store={store.search}
              placeholder={placeholder}
              className={styles.textInput}
              name='timezone-search'
              id='timezone-search'
            />
          </div>
          <TimezoneList store={store}/>
        </div>
      </ScrollableArea>
    </div>
  );
};

type InputButtonProps = {
  store: TimezoneStore;
  placeholder: string;
};
const InputButton = observer(({ store, placeholder }: InputButtonProps) => {
  const styles = useStyles(themedStyles);
  const handleShow = React.useCallback(() => {
    store.search.setValue('');
    store.modal.showModal();
  }, [store]);

  if (store.name.value && TIMEZONE_NAME_MAP[store.name.value]) {
    return (
      <button type='button' className={styles.timezoneButton} onClick={handleShow}>
        {`${TIMEZONE_NAME_MAP[store.name.value].label} (${TIMEZONE_NAME_MAP[store.name.value].offsetString})`}
      </button>
    );
  } else {
    return (
      <button type='button' className={classnames(styles.timezoneButton, styles.placeholder)} onClick={handleShow}>
        {placeholder}
      </button>
    );
  }
});

type TimezoneInputProps = {
  store: TimezoneStore;
  placeholder: string;
};

export const TimezoneInput = ({ store, placeholder }: TimezoneInputProps) => {
  return (
    <>
      <Modal store={store.modal}>
        <TimezoneModalContent store={store} placeholder={placeholder}/>
      </Modal>
      <InputButton placeholder={placeholder} store={store}/>
    </>
  );
};
